/**
 * Asset store: imported and generated video clips (spec section 33).
 *
 * Blobs live in IndexedDB; object URLs are created lazily and revoked
 * when an asset is deleted, because leaking object URLs on a phone is a
 * fast route to an out-of-memory tab.
 */

import { storage } from './db.js';
import { STORAGE } from '../core/config.js';
import { uid } from '../utils/misc.js';
import { createLogger } from '../utils/log.js';

const log = createLogger('assets');
const STORE = STORAGE.stores.assets;

/** assetId -> object URL */
const urlCache = new Map();

export function toAssetRecord({ file, blob, sceneId, providerId = 'local-library', probe = null, loopQuality = 'unknown', meta = {} }) {
  const source = blob ?? file;
  return {
    id: uid('asset'),
    sceneId,
    providerId,
    name: file?.name ?? `${sceneId}.${source.type?.includes('mp4') ? 'mp4' : 'webm'}`,
    type: source.type ?? 'video/mp4',
    size: source.size ?? 0,
    durationSeconds: probe?.duration ?? null,
    width: probe?.width ?? null,
    height: probe?.height ?? null,
    loopQuality,
    createdAt: new Date().toISOString(),
    meta,
    blob: source,
  };
}

export async function saveAsset(record) {
  await storage.put(STORE, record);
  log.info('asset saved', { id: record.id, sceneId: record.sceneId, size: record.size });
  return record;
}

export async function getAsset(id) {
  return storage.get(STORE, id);
}

export async function listAssets() {
  const all = await storage.getAll(STORE);
  return all.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

export async function listAssetsForScene(sceneId) {
  const all = await listAssets();
  return all.filter((a) => a.sceneId === sceneId);
}

/** Cached object URL for a stored asset. */
export async function getAssetUrl(id) {
  if (urlCache.has(id)) return urlCache.get(id);
  const record = await getAsset(id);
  if (!record?.blob) return null;
  const url = URL.createObjectURL(record.blob);
  urlCache.set(id, url);
  return url;
}

export function releaseAssetUrl(id) {
  const url = urlCache.get(id);
  if (url) {
    URL.revokeObjectURL(url);
    urlCache.delete(id);
  }
}

export async function deleteAsset(id) {
  releaseAssetUrl(id);
  return storage.delete(STORE, id);
}

export async function clearAssets() {
  for (const id of Array.from(urlCache.keys())) releaseAssetUrl(id);
  return storage.clear(STORE);
}

/** Total bytes used by stored assets - shown in Settings. */
export async function assetFootprint() {
  const all = await listAssets();
  return {
    count: all.length,
    bytes: all.reduce((sum, a) => sum + (a.size ?? 0), 0),
  };
}
